import { PROJECT_LOGOS } from "@/assets/static/images";
import Project from "@/types/Project";

export const callBlaze: Project = {
        logo: PROJECT_LOGOS.callBlaze,
        colors: ["#261C1C", "#8E4949"],
        name: "Call Blaze App",
        description:
            "A specialized systems-level Android application for automated telephony handling. It utilizes root-level ALSA (Advanced Linux Sound Architecture) integration to bypass OS restrictions, enabling direct audio injection into the voice stream. This project required a deep understanding of the Linux sound subsystem and Android's internal telephony services.",
        skills: ["Mobile Infrastructure"],
        tech_stack_ids: ["kotlin", "jetpack-compose", "android-architecture", "system-design"],
        started_date: "May 25’",
        ended_date: "Jun 25’",
        highlights: [
            "Root-level audio routing via ALSA, bypassing the restricted Android AudioManager for direct stream injection",
            "Low-level telephony automation using internal system services for reliable call answering",
            "High-precision audio processing: converting raw formats to telephony-compatible PCM streams",
            "Resilient foreground architecture ensuring 100% service uptime during critical automation tasks",
            "Modern Compose UI masking the underlying system-level complexity for end-users",
        ],
        accessibility: "open_source",
        url: "https://github.com/EmranHejazi/call_blaze",
        case_study: {
            slug: "call-blaze-app",
            project_name: "Call Blaze App",
            preview_video_src: "/case_studies/call_blaze/scenes/scene_1",
            thumbnail_image: PROJECT_LOGOS.callBlaze,
            hero_image: "/case_studies/call_blaze/hero_image.webp",
            hero_title: "Low-Level Systems Engineering: Android Call Automation",
            hero_subtitle:
                "Bypassing Android OS restrictions through root-level ALSA integration and system-service manipulation.",
            overview: {
                role: "Android Developer",
                team_size: "Solo Developer",
                platforms: ["Android (Rooted)"],
                summary:
                    "<strong>The Problem:</strong> Android security restrictions prevent applications from injecting audio streams directly into active voice calls. <strong>The Solution:</strong> Bypassing public SDK limits, I developed a root-level telephony automation tool integrating ALSA (Advanced Linux Sound Architecture) to inject PCM audio at the kernel mixer level. <strong>The Result:</strong> Delivered a resilient call-handling automation system with sub-50ms injection latency and automated recovery safety watchdogs.",
            },
            tech_details: {
                title: "Technical Overview",
                description:
                    "This project required moving from the standard Android SDK into the underlying Linux kernel sound subsystem.",
                architecture_highlights: [
                    "<strong>ALSA Integration:</strong> Direct hardware-level audio routing via tinyalsa and shell injection.",
                    "<strong>Telephony Internals:</strong> Using reflection and system services to manage call states beyond the public API.",
                    "<strong>PCM Processing:</strong> Real-time conversion of audio assets to 8kHz/16-bit mono streams for telephony compatibility.",
                    "<strong>Root-Shell Wrapper:</strong> A robust, error-handling wrapper for executing high-privilege system commands.",
                ],
            },
            gallery: [
                {
                    type: "video",
                    src: "/case_studies/call_blaze/scenes/scene_1",
                    caption:
                        "System-level diagnostic flow: verifying root access and ALSA mixer path availability before service initiation.",
                    muted: true,
                    autoplay: true,
                    loop: true,
                },
                {
                    type: "video",
                    src: "/case_studies/call_blaze/scenes/scene_2",
                    caption:
                        "Preferences engine: managing low-level audio routing parameters (gain, delay, looping) through a modern UI.",
                    muted: true,
                    autoplay: true,
                    loop: true,
                },
            ],
            architecture_and_scale: {
                title: "Systems Trade-offs",
                description: "When the public API says 'No', you have to weigh the risks of system-level bypasses.",
                trade_offs: [
                    {
                        decision: "ALSA vs. AudioLoopback",
                        rationale:
                            "Android's standard loopback is restricted during voice calls. I chose ALSA because it operates at the kernel level, ensuring that the audio injection remains stable regardless of the OS's attempts to mute background applications.",
                    },
                    {
                        decision: "Root Shell vs. Native C++ Wrapper",
                        rationale:
                            "For rapid iteration, I initially used root-level shell commands. Once proven, I migrated critical paths to native C++ to reduce the overhead of shell spawning and improve injection latency.",
                    },
                ],
            },
            infrastructure: {
                title: "System Integrity",
                description: "Maintaining system stability while operating with root privileges.",
                details: [
                    "<strong>Watchdog Service:</strong> Monitors the ALSA mixer to ensure system audio is restored if the app crashes.",
                    "<strong>Permission Escalation:</strong> Granular handling of Superuser requests to maintain a minimal security footprint.",
                    "<strong>Device Profiling:</strong> Automated detection of ALSA mixer paths across different SoC architectures (Qualcomm vs MediaTek).",
                ],
            },
            results: [
                {
                    metric: "Audio Injection",
                    value: "100% Success",
                    description:
                        "Achieved stable audio playback into voice calls across all verified SoC architectures.",
                    colors: ["#5ED3B3", "#4FA9D8", "#6B8CF5"],
                },
                {
                    metric: "Latency",
                    value: "< 50ms",
                    description: "Reduced the delay between call-answer and audio-start to near-instantaneous levels.",
                    colors: ["#FFB347", "#FF7A7A", "#D65BFF"],
                },
                {
                    metric: "Recovery",
                    value: "Atomic",
                    description:
                        "Guaranteed restoration of system audio paths via the hardware-level watchdog service.",
                    colors: ["#2BD9A4", "#1FBF89", "#2A9D8F"],
                },
            ],
            challenge: {
                title: "The Problem & Constraints",
                description:
                    "Standard Android APIs prevent audio injection into calls to prevent eavesdropping and spoofing. My constraint was to enable this for legitimate automation while maintaining system stability on rooted devices.",
                points: [
                    "<strong>Problem:</strong> Public AudioManager mutes all app-layer audio during an active voice call.",
                    "<strong>Constraint:</strong> The solution must work without custom ROMs, relying only on standard root access.",
                    "<strong>Challenge:</strong> Managing 'Mixer Path' variability across hundreds of different Android device models.",
                    "<strong>Risk:</strong> Potential for permanent audio-silence if ALSA paths aren't correctly reset after a call.",
                ],
            },
            conclusion: {
                title: "Conclusion & Reflection",
                content:
                    "Call Blaze demonstrated my ability to operate at the system level. It required bridging the gap between high-level Kotlin/Compose and the low-level Linux kernel sound subsystem. It's a testament to 'Getting it done' when the platform says it's impossible.",
                key_takeaways: [
                    "<strong>Know the Kernel:</strong> Understanding what happens below the SDK is the key to solving 'Impossible' problems.",
                    "<strong>Safety First:</strong> High-privilege code requires twice the amount of error-handling and recovery logic.",
                    "<strong>Abstraction:</strong> A clean UI can (and should) hide a massive amount of underlying technical complexity.",
                ],
            },
        },

    };
